import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { FlashMessage, MessageLevel } from '../flash.message.record';
import { LogLevel, LogMessage, LogService } from '../../../shared/log/log.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * This will display a message to the user.
 *
 * If it is an error it will wait for the user to click close before removing.
 *
 * Usage:
 *
 * this.flashMessagesService.info('We are in about component!');
 * this.flashMessagesService.error('Failure!');
 * this.flashMessagesService.warn('Warn!');
 * this.flashMessagesService.success('Success!');
 */
@Injectable()
export class FlashMessagesService {
  private messageStream: Subject<FlashMessage>;

  constructor(private logService: LogService, private translateService: TranslateService) {
    // Create a new Subject and expose the stream as an Observable.
    this.messageStream = new Subject();
  }

  get messages(): Observable<FlashMessage> {
    return this.messageStream.asObservable();
  }

  public post(flashMessage: FlashMessage) {
    // Add the message to the log stream
    this.messageStream.next(flashMessage);
  }

  public error(flashMessage: string, logMessage: string = null, translateParams = {}) {
    this.logMessage(LogLevel.ERROR, logMessage);
    this.flashMessage(MessageLevel.ERROR, flashMessage, false, false, translateParams);
  }

  public warn(flashMessage: string, logMessage: string = null, translateParams = {}) {
    this.logMessage(LogLevel.WARN, logMessage);
    this.flashMessage(MessageLevel.WARN, flashMessage, false, false, translateParams);
  }

  public info(flashMessage: string, logMessage: string = null, translateParams = {}) {
    this.logMessage(LogLevel.INFO, logMessage);
    this.flashMessage(MessageLevel.INFO, flashMessage, false, false, translateParams);
  }

  public success(flashMessage: string, logMessage: string = null, translateParams = {}) {
    this.logMessage(LogLevel.VERBOSE, logMessage);
    this.flashMessage(MessageLevel.SUCCESS, flashMessage, true, true, translateParams);
  }

  private flashMessage(level, message, displayThroughNavigation: boolean, autoClose: boolean, translateParams = {}) {
    /**
     * .subscribe is used instead of .instant because of an issue that exists
     * when a flash message needs to be thrown during ngOnInit.  If .instant is used,
     * the label will be untranslated.
     */
    this.translateService.get(message, translateParams).subscribe(translation => {
      this.post(new FlashMessage(level, translation, displayThroughNavigation, autoClose));
    });
  }

  private logMessage(level, message) {
    if (message) {
      this.logService.log(new LogMessage(level, message));
    }
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/shared/flash-messages/service/flash.messages.service.ts